<template lang="pug">
UpsertPage(
  title="Shop settings"
  @save="validateAndSave"
  @cancel="$emit('cancel')"
  :pending="isSaving"
  :hideCancel="true"
)
  BriqPanel.mb-S.shop-settings-panel
    BriqMultilanguageWarningModal(
      v-if="hasMultipleLanguages"
      v-show="showWarningModal"
      :defaultModel="shopSettings"
      :translationInfo="shopSettings.TranslationInfo"
      :translatableProperties="translatableProperties"
      @close="showWarningModal = false" 
      @onSubmit="$emit('save')"
      )
    .translation-header(v-if="hasMultipleLanguages")
      BriqButton.translation-button(
        small
        variant="secondary"
        @click="toggleTranslation"
        data-test-id="translation-button"
        ) 
          .flex
            .translation-icon.mr-XXS
              BriqIcon(:name="`icon-input-${toggleTranslationIcon}-icon`") 
            .translation-text {{ toggleTranslationText }}
    BriqMultilanguage(
      v-model="activeCulture"
      :translationFields="translationFields"
      :cultures="shopSettings.TranslationInfo.AvailableCultures"
      :showSecondColumn="shouldShowMultilanguage"
      :defaultCulture="defaultCultureDisplayName"
      )
      ShopSettingsFields(:shopSettings="shopSettings" :resourceFilters="resourceFilters" ref="ShopSettingsFields")
      template(v-slot:secondary)
        ShopSettingsFields(
          v-if="activeCulture"
          :shopSettings="translationModel"
          :resourceFilters="resourceFilters"
          :translationSections="translationSections"
          )
</template>

<script>
import UpsertPage from '@v/components/UpsertPage.vue'
import ShopSettingsFields from '@v/settings/application/shop/ShopSettingsFields.vue'
import BriqMultilanguage from '@/vue/components/BriqMultilanguage.vue'
import completeTranslationInfo from '@/vue/util/completeTranslationInfo.js'
import BriqMultilanguageWarningModal from '@/vue/components/BriqMultilanguageWarningModal.vue'
import { emptyTranslatableFieldsExist } from '@v/util/emptyTranslatableFields.js'
import { clone, isEmpty } from 'ramda'

export default {
  components: {
    UpsertPage,
    ShopSettingsFields,
    BriqMultilanguage,
    BriqMultilanguageWarningModal,
  },
  props: {
    shopSettings: Object,
    resourceFilters: Object,
    isSaving: Boolean,
    enableMultilanguage: Boolean, // feature flag
  },
  data() {
    return {
      showTranslations: false,
      activeCulture: this.shopSettings.TranslationInfo?.AvailableCultures?.[0],
      showTranslationsAgain: false,
      showWarningModal: false,
    }
  },
  computed: {
    hasMultipleLanguages() {
      return (
        this.enableMultilanguage && !!this.shopSettings.TranslationInfo?.AvailableCultures?.length
      )
    },
    areAnyAvailableCultures() {
      return typeof this.activeCulture !== 'undefined'
    },
    toggleTranslationText() {
      const what = this.showTranslations ? 'Hide' : 'Show'
      return what + ' translations'
    },
    toggleTranslationIcon() {
      return this.showTranslations ? 'eye-crossed' : 'eye-open'
    },
    shouldShowMultilanguage() {
      return this.enableMultilanguage && this.showTranslations
    },
    translationSections() {
      return ['Filter - Occasions', 'Custom filters']
    },
    defaultCulture() {
      return this.shopSettings.TranslationInfo?.DefaultCulture
    },
    defaultCultureDisplayName() {
      return this.defaultCulture ? this.defaultCulture?.DisplayName : ''
    },
    translationFields() {
      // todo: refactor to make it work with model field name instead of label
      return ['Filter label', 'Option label', 'Filter question', 'Occasion label']
    },
    translationModel() {
      if (!this.activeCulture) return null
      const key = this.activeCulture.Name
      return this.shopSettings.TranslationInfo.Translations[key]
    },
    translatableProperties() {
      return [
        {
          label: 'Custom Filters',
          key: 'Filters',
        },
        {
          label: 'Filter - Occasions',
          key: 'Theme',
        },
      ]
    },
  },
  methods: {
    isEmpty: isEmpty,
    validateAndSave() {
      this.$refs.ShopSettingsFields.$v.$touch()
      if (this.$refs.ShopSettingsFields.$v.shopSettings.$invalid) {
        this.$notifyError('The values of some of the fields are invalid, please check them!')
      } else {
        if (
          this.hasMultipleLanguages &&
          emptyTranslatableFieldsExist(
            this.shopSettings,
            this.shopSettings.TranslationInfo,
            this.translatableProperties,
          )
        ) {
          this.showWarningModal = true
        } else {
          this.$emit('save')
        }
      }
    },
    toggleTranslation() {
      this.showTranslations = !this.showTranslations
    },
    fillTranslationsFieldsForThemesAndFilters() {
      if (typeof this.shopSettings.TranslationInfo.AvailableCultures === 'undefined') return false
      for (const [key, availableCulture] of Object.entries(
        this.shopSettings.TranslationInfo.AvailableCultures,
      )) {
        if (this.shopSettings.TranslationInfo.Translations[availableCulture.Name].Theme === null) {
          this.shopSettings.TranslationInfo.Translations[
            availableCulture.Name
          ].Theme = this.copyOfCleanTranslatableThemeObject()
        }

        if (
          this.shopSettings.TranslationInfo.Translations[availableCulture.Name].Filters === null
        ) {
          this.shopSettings.TranslationInfo.Translations[
            availableCulture.Name
          ].Filters = this.copyOfCleanTranslatableFiltersObject()
        }
      }
    },
    copyOfCleanTranslatableThemeObject() {
      const themeObject = clone(this.shopSettings.Theme)
      for (const [key, value] of Object.entries(themeObject)) {
        themeObject[key].name = ''
      }
      return themeObject
    },
    copyOfCleanTranslatableFiltersObject() {
      const filtersObject = clone(this.shopSettings.Filters)
      for (const [filterKey, filter] of Object.entries(this.shopSettings.Filters)) {
        filtersObject[filterKey].name = ''
        filtersObject[filterKey].question = ''

        for (const [rowKey, filterRow] of Object.entries(filter.rows)) {
          filtersObject[filterKey].rows[rowKey].name = ''
        }
      }
      return filtersObject
    },
  },
  beforeMount() {
    completeTranslationInfo(this.shopSettings)
    this.fillTranslationsFieldsForThemesAndFilters()
  },
}
</script>

<style scoped lang="stylus">
.translation-header
  display flex
  width 100%
  justify-content space-between
  flex-direction row-reverse

  .translation-button
    margin-bottom 20px
</style>

<style lang="stylus">
.shop-settings-panel
  .briq-form-element
    margin-bottom 24px
</style>
